/** Pinia Store para la gestión de Avisos de Privacidad relacionados con el Buró de Crédito. */
import { defineStore } from 'pinia';
import { getCurrentInstance } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { useAuthStore } from '@/pinia/auth.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { getNestedValue } from '@/helpers/sort';
import * as service from '@/services';
import html2pdf from 'html2pdf.js';

const $toast = useToast();

export const usePrivacyPolicyStore = defineStore('privacyPolicy', {
    /**
     * Estado central del store.
     */
    state: () => ({
        searchString: '',
        isModalOpen: false,
        privacyPolicies: [],
        loanApplicationsOptions: [],
        errors: null,
        sortKey: '',
        sortOrder: 1, // 1 for ascending, -1 for descending
        itemsPerPage: 10, //Por default 10 por pagina
        currentPage: 1,
        // Nuevos estados para el aviso de privacidad
        statusId: 21, //Por default activo
        nombre: '',
        fecha: '',
        firma: '',
        logo: '/img/LOGOTIPOGOLDERGFUND.jpg',
        avisoContent: null,
        numReference: "",
        policyId: null,
        // Combo de solicitudes
        selectedApplication: null,
        //Estados de la modal
        isEdit: false,
        // Configuración del store
        errors: null
    }),
    /**
     * Getters para filtrar, ordenar y paginar los avisos.
     */
    getters: {
        /**
         * Devuelve las políticas filtradas por el texto de búsqueda.
         */
        filteredPolicies: (state) => {
            if (!state.searchString) return state.privacyPolicies;

            //#region control de fecha string
            const instance = getCurrentInstance();
            const $filters = instance?.appContext.config.globalProperties.$filters;
            //#endregion control de fecha string

            const searchLower = state.searchString.toLowerCase();
            return state.privacyPolicies.filter(policy => {

                const firstNameProspect = policy.loanApplication?.prospectus?.generalInformation?.firstName ? policy.loanApplication?.prospectus?.generalInformation?.firstName?.toLowerCase() : '';
                const lastNameProspect = policy.loanApplication?.prospectus?.generalInformation?.lastName ? policy.loanApplication?.prospectus?.generalInformation?.lastName?.toLowerCase() : '';
                const mothersMaidenNameProspect = policy.loanApplication?.prospectus?.generalInformation?.mothersMaidenName ? policy.loanApplication?.prospectus?.generalInformation?.mothersMaidenName?.toLowerCase() : '';
                const nameProspect = policy.loanApplication?.prospectus?.generalInformation?.businessName === null ?
                    `${firstNameProspect} ${lastNameProspect} ${mothersMaidenNameProspect}`
                    :
                    policy.loanApplication?.prospectus?.generalInformation?.businessName ? policy.loanApplication?.prospectus?.generalInformation?.businessName?.toLowerCase() : '';

                return (
                    policy.referenceNumber?.toLowerCase().includes(searchLower) ||
                    policy.loanApplication?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    policy.loanApplication?.prospectus?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(policy.dateSignature)?.toLowerCase().includes(searchLower) ||
                    nameProspect.includes(searchLower) ||
                    policy.loanApplication?.prospectus?.personType?.name?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(policy.createdAt)?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(policy.updatedAt)?.toLowerCase().includes(searchLower) ||
                    policy.status?.name?.toLowerCase().includes(searchLower)
                );
            });
        },
        /**
         * Ordena las políticas según la clave de orden actual.
         */
        sortedPolicies: (state) => {
            // Logica de ordenamiento
            let sortedList = [...state.filteredPolicies];
            if (state.sortKey) {
                sortedList.sort((a, b) => {

                    // Caso especial para el nombre
                    if (state.sortKey === 'name') {
                        const getNameForSort = (item) => {
                            if (!item.loanApplication?.prospectus) return '';
                        
                            if (item.loanApplication.prospectus.personType?.id === 1) {
                                return `${item.loanApplication.prospectus.generalInformation?.firstName || ''} ${
                                item.loanApplication.prospectus.generalInformation?.lastName || ''} ${
                                item.loanApplication.prospectus.generalInformation?.mothersMaidenName || ''}`.toLowerCase();
                            } else {
                                return (item.loanApplication.prospectus.generalInformation?.businessName || '').toLowerCase();
                            }
                        };
                        
                        const aName = getNameForSort(a);
                        const bName = getNameForSort(b);
                        if (aName < bName) return -1 * state.sortOrder;
                        if (aName > bName) return 1 * state.sortOrder;
                        return 0;
                    }
                    
                    // Ordenamiento normal para otras columnas
                    let aValue = getNestedValue(a, state.sortKey);
                    let bValue = getNestedValue(b, state.sortKey);

                    if (aValue < bValue) return -1 * state.sortOrder;
                    if (aValue > bValue) return 1 * state.sortOrder;
                    return 0;
                });
            }
            return sortedList;
        },
        /**
         * Aplica paginación a la lista ordenada.
         */
        paginatedPolicies: (state) => {
            if (!state.sortedPolicies || state.sortedPolicies.length === 0) return [];
            let start = (state.currentPage * state.itemsPerPage) - state.itemsPerPage;
            return state.sortedPolicies.slice(start, start + state.itemsPerPage);
        }
    },
    /**
     * Acciones asincrónicas y mutaciones del store.
     */
    actions: {
        /**
         * Obtiene las opciones de solicitudes de crédito disponibles para llenar el combo de selección de la modal de aviso de privacidad.
         * http://localhost:5122/api/CreditBureauPrivacyPolicy/filterLoanApplications/1 ${userAuth.user.companyId}
         */
        async filterBureauLoanApplications() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.privacyPolicyApi.get(`/api/CreditBureauPrivacyPolicy/filterLoanApplications/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.loanApplicationsOptions = data.data;
                }
                useLoadingStore().loading = false;
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Obtiene todos los avisos de privacidad registrados.
         * http://localhost:5122/api/CreditBureauPrivacyPolicy/all/${useAuthStore().user.companyId}
         */
        async fetchPrivacyPolicies() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.privacyPolicyApi.get(`/api/CreditBureauPrivacyPolicy/all/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.privacyPolicies = data.data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Crea un nuevo regisro de aviso de privacidad.
         * http://localhost:5122/api/CreditBureauPrivacyPolicy
         */
        async createBureauPrivacyPolicy(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.privacyPolicyApi.post(`/api/CreditBureauPrivacyPolicy`, payload);
                if (data && data.status) {
                    $toast.success(getLocaleMessage('privacyPolicyApi', 'creatingSuccess'));
                    this.privacyPolicies.unshift(data.data);
                    return data;
                } else {
                    $toast.success(getLocaleMessage('privacyPolicyApi', 'creatingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Actualiza un registro de aviso de privacidad existente.
         * http://localhost:5122/api/CreditBureauPrivacyPolicy
         */
        async updateBureauPrivacyPolicy(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.privacyPolicyApi.put(`/api/CreditBureauPrivacyPolicy`, payload);
                if (data && data.status) {
                    const index = this.privacyPolicies.findIndex(c => c.id === payload.id);
                    if (index !== -1) {
                        this.privacyPolicies.splice(index, 1, data.data);
                    }
                    $toast.success(getLocaleMessage('privacyPolicyApi', 'editingSuccess'));
                    return data.data;
                } else {
                    $toast.success(getLocaleMessage('privacyPolicyApi', 'editingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Abre el modal para crear nuevo registro de aviso privacidad.
         * Acción por el boton Generar
         * Abrir modal de creación isEdit = false
         */
        async openModal() {
            this.isModalOpen = true;
            this.isEdit = false;
            await this.filterBureauLoanApplications(); //Refrescar los datos del combo de selección
        },
        /**
         * Cierra el modal y limpia estados relacionados.
         */
        async closeModal() {
            this.isModalOpen = false;
            this.selectedApplication = null;
            this.nombre = '';
            this.fecha = '';
            await this.fetchPrivacyPolicies(); //Refrescar datos de las tablas
        },
        /**
         * Maneja errores en la comunicación con la API.
         */
        handleError(error) {
            this.errors = error;
            if (error.response) {
                $toast.error(`${getLocaleMessage('common', 'responseError')} ${error.response.data}`);
            } else if (error.request) {
                $toast.error(`${getLocaleMessage('common', 'networkError')} ${error.response.data}`);
            } else {
                $toast.error(`${getLocaleMessage('common', 'generalError')} ${error.response.data}`);
            }
        },
        /**
         * Cambia el orden de las columnas en la tabla.
         */
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortOrder = 1;
            }
            this.sortKey = key;
            this.currentPage = 1; // Resetear a la primera página al ordenar
        },
        /**
         * Establece la página actual del paginador.
         */
        setCurrentPage(page) {
            this.currentPage = page;
        },
        /**
         * Carga los datos de una política existente para edición.
         * Abrir modal de edición isEdit = true
         */
        editPolicy(policy) {
            // Lógica para editar una política
            // Puede abrir un modal o redirigir a otra vista
            this.isEdit = true;
            this.selectedApplication = policy.loanApplication;
            this.numReference = policy.loanApplication.referenceNumber;
            this.nombre = policy.loanApplication.prospectus.personType.id === 1 ?
                `${policy.loanApplication.prospectus.generalInformation.firstName} ${policy.loanApplication.prospectus.generalInformation.lastName} ${policy.loanApplication.prospectus.generalInformation.mothersMaidenName}` : `${policy.loanApplication.prospectus.generalInformation.businessName}`;
            this.fecha = policy.dateSignature;
            this.policyId = policy.id;
        },
        /**
         * Acción para guarda los datos del formulario, ya sea creando o actualizando.
         */
        async saveData() {
            try {
                useLoadingStore().loading = true;
                let data = {
                    loanApplicationId: this.selectedApplication.id,
                    statusId: 21,
                    dateSignature: this.fecha,
                    placeSignature: ""
                };

                let response;

                if (this.isEdit) {
                    if (this.policyId !== null) {
                        data.id = this.policyId;
                    }
                    response = await this.updateBureauPrivacyPolicy(data);
                } else {
                    response = await this.createBureauPrivacyPolicy(data);
                }

                if (response && response.status) {
                    this.downloadPDF();
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Acción para descarga el PDF del aviso de privacidad.
         */
        async downloadPDF() {
            if (!this.avisoContent) return;

            /**Ocultar el boton de descarga */
            const formActions = document.querySelector('.form-actions');
            if (formActions) formActions.style.display = 'none';

            /**Ocultar la linea sepradora ---- de pagina */
            const breackPage = document.querySelectorAll('.separado-page');
            if (breackPage) {
                breackPage.forEach(el => {
                    el.style.display = 'none';
                });
            }

            /**Ayuda meter el salto de pagina para mandar el contenido a otra pagina */
            const pageA4 = document.querySelectorAll('.pagina');
            if (pageA4) {
                pageA4.forEach(el => {
                    el.classList.add('mode-print');
                });
            }

            /**Ayuda a eliminar una pagina en blanco si es la ultima pagina */
            // const lastPage = pageA4[pageA4.length - 1];
            // if (lastPage) {
            //     const isEmpty = !lastPage.innerText.trim() && !lastPage.querySelector('img, input, textarea, p, span');
            //     if (isEmpty) lastPage.remove();
            // }

            const opt = {
                margin: 0,
                filename: 'Aviso_de_Privacidad.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    logging: true,
                    useCORS: true,
                    letterRendering: true
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4',
                    orientation: 'portrait'
                }
            };

            try {
                useLoadingStore().loading = true;
                await html2pdf().set(opt).from(this.avisoContent).save();
            } catch (error) {
                useLoadingStore().loading = false;
                this.handleError(error);
            } finally {
                useLoadingStore().loading = false;

                /**Restableciendo diseños de origen en la modal */

                if (formActions) formActions.style.display = 'block';

                if (breackPage) {
                    breackPage.forEach(el => {
                        el.style.display = 'block';
                    });
                }


                if (pageA4) {
                    pageA4.forEach(el => {
                        el.classList.remove('mode-print');
                    });
                }

                /**
                 * Nota: Con esta parte del codigo soluciono el detalle de cuando se mantiene
                 * abierto la modal de creación despues dar descargar y este se manda a GUARDAR 
                 * si el usuario quiere modificar algun campo y mandar descargar de nuevo con la
                 * nueva captura en alguno de los campos no deja porque le marca error de solicitud
                 * ya existente.
                 * Opción 1:
                 * Comportamiento: Una vez creado el registro y como se mantiene la modal abierta
                 * por automatico cuando se termina de GUARDAR el registro en la BD este se convierte
                 * en una modal de edición. Si ese es el comportamiento adecuado dejar este codigo
                 * Opción 2:
                 * Comportamiento: Una vez creado el registro y como se mantiene la modal abierta
                 * y sigue siendo una creación este al querer editar un dato y de descargar lo sigue
                 * reconociendo como una creación y marca error porque dice que ya existe el uso
                 * de esa solicitud dentro del proceso de aviso de privacidad. Si ese es el
                 * comportamiento adecuado retirar este codigo
                 */
                //this.isEdit = true; //indica vuelvelo edición
                //this.policyId = this.privacyPolicies[0].id; //setea el valor del nuevo registro creado
                //this.numReference = this.privacyPolicies[0].loanApplication.referenceNumber; //setea el valor de numero de solicitud
            }
        },
        /**
         * Establece la referencia al contenido del aviso.
         */
        setAvisoContent(ref) {
            this.avisoContent = ref;
        }
    }
});